import { queryOptions, infiniteQueryOptions } from "@tanstack/react-query";
import { getSupabase } from "@/shared/api/supabase";
import { parseRow, parseRows } from "@/shared/lib/parse";
import {
  ProductCardSchema,
  ProductSchema,
  ZERO_UUID_SENTINEL,
  type Product,
  type ProductCard,
  type CatalogFilters,
} from "@/shared/lib/db-schema";
import { keys } from "@/shared/lib/query-keys";
import type { Database } from "@/shared/types/database.types";
import { CATALOG_PAGE_LIMIT, CATALOG_DEFAULT_SORT } from "@/shared/config/catalog";

// The filters store uses ZERO_UUID_SENTINEL to mean "not selected / all".
// The RPC filters by `col = any(arr)`, so a sentinel must become a null
// (no filter) — otherwise it matches nothing and the catalog is empty.
function filterArray(v: readonly string[] | null | undefined): string[] | null {
  if (!v) return null;
  const cleaned = v.filter((x) => x !== "" && x !== ZERO_UUID_SENTINEL);
  return cleaned.length > 0 ? cleaned : null;
}

const PRODUCT_COLS =
  "id,article,name,description,category_id,gender_id,country_id,brand_ids,color_ids,size_ids,material_ids,season_ids,base_price,old_price,badge,status,composition,created_at,updated_at,deleted_at";

export async function fetchProducts(f: Partial<CatalogFilters> = {}): Promise<ReadonlyArray<ProductCard>> {
  const params = {
    p_categories: filterArray(f.categories),
    p_genders:    filterArray(f.genders),
    p_brands:     filterArray(f.brands),
    p_colors:     filterArray(f.colors),
    p_sizes:      filterArray(f.sizes),
    p_materials:  filterArray(f.materials),
    p_seasons:    filterArray(f.seasons),
    p_min_price:  f.minPrice ?? null,
    p_max_price:  f.maxPrice ?? null,
    p_search:     f.search ?? null,
    p_badge:      f.badge ?? null,
    p_sort:       f.sort ?? CATALOG_DEFAULT_SORT,
    p_limit:      f.limit && f.limit > 0 ? f.limit : CATALOG_PAGE_LIMIT,
    p_offset:     f.offset ?? 0,
  };
  const { data, error } = await getSupabase().rpc(
    "rpc_catalog_filter",
    params as unknown as Database["public"]["Functions"]["rpc_catalog_filter"]["Args"],
  );
  if (error) throw error;
  return parseRows(ProductCardSchema, data ?? [], "ProductCard");
}

async function fetchProductById(id: string): Promise<Product | null> {
  const { data, error } = await getSupabase()
    .from("products")
    .select(PRODUCT_COLS)
    .eq("id", id)
    .eq("status", "active")
    .is("deleted_at", null)
    .maybeSingle();
  if (error) throw error;
  return data ? parseRow(ProductSchema, data, "Product") : null;
}

/** Infinite catalog list (offset pagination via RPC). */
export function productsInfiniteOptions(filters: Partial<CatalogFilters>) {
  return infiniteQueryOptions({
    queryKey: keys.product.list(filters),
    queryFn: ({ pageParam }) => fetchProducts({ ...filters, limit: CATALOG_PAGE_LIMIT, offset: pageParam }),
    initialPageParam: 0,
    // Accumulate the offset across pages (fixes the prior repeat-page bug).
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < CATALOG_PAGE_LIMIT
        ? undefined
        : allPages.reduce((n, p) => n + p.length, 0),
  });
}

/** Flat product list (e.g. favorites resolution). */
export function productsListOptions(filters: Partial<CatalogFilters> = {}) {
  return queryOptions({
    queryKey: keys.product.list(filters),
    queryFn: () => fetchProducts(filters),
  });
}

/** Single product by id. */
export function productByIdOptions(id: string) {
  return queryOptions({
    queryKey: keys.product.detail(id),
    queryFn: () => fetchProductById(id),
  });
}
